import { makeStyles } from '@material-ui/core/styles'

import { ChatMode } from '../../core/types/chat.types'
import useMaxEmptyStatePhrases from '../../hooks/use-max-empty-state-phrases'
import AvatarIcon from '../chat/avatar-icon'

type OpenChatEmptyStateProps = {
  onModeSelect?: (mode: ChatMode) => void
}

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: 'var(--ds-bg-subtle, #f1f2f7)',
  },
  inner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3.5),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.75),
  },
  greetingBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  greeting: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.2,
    color: 'var(--ds-fg-primary, #1f2430)',
    margin: 0,
  },
  subGreeting: {
    fontSize: '0.875rem',
    fontWeight: 400,
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    margin: 0,
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(1.25),
  },
  card: {
    background: 'var(--ds-bg-surface, #fff)',
    border: '1px solid rgba(220,223,232,0.95)',
    borderRadius: 14,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'box-shadow 260ms ease, transform 150ms linear, border-color 260ms ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(15,23,42,0.1)',
      transform: 'translateY(-2px)',
      borderColor: 'var(--color-coral-sender, #E8735A)',
    },
  },
  cardLabel: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--color-coral-sender, #E8735A)',
    margin: 0,
  },
  cardText: {
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: 'var(--ds-fg-primary, #1f2430)',
    margin: 0,
  },
}))

const suggestionCards: Array<{ mode: ChatMode; label: string; text: string }> = [
  { mode: 'COMPARE', label: 'Analyze', text: 'Compare budgets across 3 campaigns' },
  { mode: 'EXPLORE', label: 'Explore', text: 'What is our avg VFX cost this quarter?' },
  { mode: 'BUDGET', label: 'Budget', text: 'Create a budget allocation plan' },
  { mode: 'DEFAULT', label: 'OpenChat', text: 'Explain production complexity scoring' },
]

const OpenChatEmptyState = ({ onModeSelect }: OpenChatEmptyStateProps) => {
  const classes = useStyles()
  const { greeting } = useMaxEmptyStatePhrases()

  return (
    <div className={classes.root}>
      <div className={classes.inner}>
        <div className={classes.header}>
          <AvatarIcon size={56} />
          <div className={classes.greetingBlock}>
            <p className={classes.greeting}>{greeting}</p>
            <p className={classes.subGreeting}>How can I help you today?</p>
          </div>
        </div>

        <div className={classes.cards}>
          {suggestionCards.map((card) => (
            <button
              key={card.mode}
              type="button"
              className={classes.card}
              onClick={() => onModeSelect?.(card.mode)}
            >
              <p className={classes.cardLabel}>{card.label}</p>
              <p className={classes.cardText}>{card.text}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default OpenChatEmptyState
